import React, { useMemo, useCallback } from 'react';

import { NavButtons } from '../common';
import { generateEmailHtml } from './GenerateSection';
import {
  compileAccessibilityReport,
  canFixContrastItem,
  applyContrastFix,
  canFixHeadingIssues,
  applyHeadingFix,
} from '../../utils/accessibility';

const AccessibilitySection = ({
  settings,
  onUpdate,
  onNavigate,
  globalSettings,
}) => {
  const generatedHtml = useMemo(() => {
    if (!settings || !globalSettings) {
      return '';
    }
    return generateEmailHtml(settings, globalSettings);
  }, [settings, globalSettings]);

  const { contrast: contrastFindings, headingFindings, hasWarnings } = useMemo(
    () => compileAccessibilityReport(settings, generatedHtml),
    [settings, generatedHtml]
  );

  const failingContrast = useMemo(
    () => contrastFindings.filter(item => !item.passes),
    [contrastFindings]
  );

  const headingFixAvailable = useMemo(
    () => canFixHeadingIssues(headingFindings, settings),
    [headingFindings, settings]
  );

  const hasFixableIssues = useMemo(
    () =>
      failingContrast.some(item => canFixContrastItem(item, settings)) ||
      headingFixAvailable,
    [failingContrast, headingFixAvailable, settings]
  );

  const handleFixContrast = useCallback(
    itemId => {
      if (!settings) return;
      const target = contrastFindings.find(item => item.id === itemId);
      if (!target) return;
      if (!canFixContrastItem(target, settings)) return;
      const { settings: nextSettings, changed } = applyContrastFix(settings, target);
      if (changed) {
        onUpdate(null, nextSettings);
      }
    },
    [contrastFindings, onUpdate, settings]
  );

  const handleFixHeading = useCallback(() => {
    if (!headingFixAvailable || !settings) return;
    const { settings: nextSettings, changed } = applyHeadingFix(settings);
    if (changed) {
      onUpdate(null, nextSettings);
    }
  }, [headingFixAvailable, onUpdate, settings]);

  const handleFixAllAccessibility = useCallback(() => {
    if (!settings) return;
    let nextSettings = settings;
    let mutated = false;

    failingContrast.forEach(item => {
      if (!canFixContrastItem(item, nextSettings)) return;
      const { settings: updatedSettings, changed } = applyContrastFix(
        nextSettings,
        item
      );
      if (changed) {
        nextSettings = updatedSettings;
        mutated = true;
      }
    });

    if (headingFixAvailable && headingFindings.length > 0) {
      const { settings: updatedSettings, changed } = applyHeadingFix(nextSettings);
      if (changed) {
        nextSettings = updatedSettings;
        mutated = true;
      }
    }

    if (mutated) {
      onUpdate(null, nextSettings);
    }
  }, [
    failingContrast,
    headingFindings,
    headingFixAvailable,
    onUpdate,
    settings,
  ]);

  if (!settings || !globalSettings) {
    return (
      <div className="glass-panel p-6 text-sm text-slate-600">
        Accessibility tools will be available once the template settings and
        global assets finish loading.
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="glass-panel p-6 space-y-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="space-y-2">
            <p className="text-xs font-semibold uppercase tracking-[0.4em] text-slate-500">
              Accessibility
            </p>
            <h3 className="text-lg font-semibold text-slate-900">Accessibility Checker</h3>
            <p className="text-sm text-slate-500">
              Review colour contrast and heading hierarchy before exporting your
              campaign.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <span
              className={`pill-tag ${
                hasWarnings
                  ? 'bg-amber-200 text-amber-800'
                  : 'bg-emerald-100 text-emerald-700'
              }`}
            >
              {hasWarnings ? 'Attention needed' : 'All checks passed'}
            </span>
            {hasFixableIssues ? (
              <button
                type="button"
                onClick={handleFixAllAccessibility}
                className="inline-flex items-center rounded-lg border border-primary/40 bg-primary/10 px-4 py-2 text-sm font-semibold text-primary transition hover:border-primary hover:bg-primary/20"
              >
                Fix all issues
              </button>
            ) : null}
          </div>
        </div>

        <div className="space-y-8">
          <section className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h4 className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                Contrast
              </h4>
            </div>
            <div className="space-y-3">
              {contrastFindings.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white/70 p-4 text-sm text-slate-500">
                  No contrast data available for this template.
                </div>
              ) : (
                contrastFindings.map(item => {
                  const canFix = !item.passes && canFixContrastItem(item, settings);
                  return (
                    <div
                      key={item.id}
                      className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white/80 p-4 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{item.label}</p>
                        <p className="mt-1 text-xs text-slate-500">
                          {item.context ? `${item.context} • ` : ''}
                          {item.ratio !== null
                            ? `${item.ratio}:1 contrast`
                            : 'Unable to compute contrast (invalid colour)'}
                        </p>
                      </div>
                      <div className="flex flex-col items-start gap-2 sm:flex-row sm:items-center">
                        <span
                          className={`pill-tag ${
                            item.passes
                              ? 'bg-emerald-100 text-emerald-700'
                              : 'bg-amber-200 text-amber-800'
                          }`}
                        >
                          {item.passes ? 'Pass' : 'Needs attention'}
                        </span>
                        {canFix ? (
                          <button
                            type="button"
                            onClick={() => handleFixContrast(item.id)}
                            className="inline-flex items-center rounded-lg border border-primary/30 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:border-primary hover:bg-primary/10"
                          >
                            Fix contrast
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </section>

          <section className="space-y-4">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h4 className="text-xs font-semibold uppercase tracking-[0.35em] text-slate-500">
                Heading Structure
              </h4>
              <div className="flex items-center gap-2">
                <span
                  className={`pill-tag ${
                    headingFindings.length === 0
                      ? 'bg-emerald-100 text-emerald-700'
                      : 'bg-amber-200 text-amber-800'
                  }`}
                >
                  {headingFindings.length === 0 ? 'Pass' : 'Needs attention'}
                </span>
                {headingFindings.length > 0 && headingFixAvailable ? (
                  <button
                    type="button"
                    onClick={handleFixHeading}
                    className="inline-flex items-center rounded-lg border border-primary/30 bg-white px-3 py-1.5 text-xs font-semibold text-primary transition hover:border-primary hover:bg-primary/10"
                  >
                    Fix heading order
                  </button>
                ) : null}
              </div>
            </div>
            <div className="space-y-3">
              {headingFindings.length === 0 ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700">
                  Headings follow a logical order.
                </div>
              ) : (
                headingFindings.map((issue, index) => {
                  const isError = issue.type === 'error';
                  return (
                    <div
                      key={`heading-issue-${index}`}
                      className={`rounded-2xl border p-4 text-sm ${
                        isError
                          ? 'border-rose-200 bg-rose-50 text-rose-700'
                          : 'border-amber-200 bg-amber-50 text-amber-800'
                      }`}
                    >
                      <p>{issue.message}</p>
                      {issue.text && issue.text.length > 0 ? (
                        <span className="mt-2 block text-xs text-slate-600">
                          Sample text: “{issue.text}”
                        </span>
                      ) : null}
                    </div>
                  );
                })
              )}
            </div>
          </section>
        </div>
      </div>

      <NavButtons
        onPrev={() => onNavigate('contacts')}
        onNext={() => onNavigate('generate')}
        prevLabel="Previous: Contact Information"
        nextLabel="Next: Generate HTML"
      />
    </div>
  );
};

export default AccessibilitySection;
